import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const ACCENT = "#F59E0B";
const NAVY = "#0D1B2A";
const GRAY = "#64748B";
const GREEN = "#16A34A";
const RED = "#DC2626";

/**
 * Page de réinitialisation de mot de passe
 *
 * Fonctionnement :
 * 1. Supabase redirige vers bativiz.fr#access_token=...&type=recovery
 * 2. Le client Supabase JS consomme le hash et établit une session
 * 3. Ce composant attend que la session soit prête via onAuthStateChange
 * 4. Une fois la session active, l'utilisateur peut changer son mot de passe
 */
export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(false);

  // ─── Attente de la session recovery ───
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Vérifier si une session existe déjà (cas rare mais possible)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });

    // Écouter onAuthStateChange — Supabase fire PASSWORD_RECOVERY
    // une fois qu'il a traité le hash et établi la session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY" && session) {
          setSessionReady(true);
        }
        // Couvrir aussi le cas où la session arrive via un autre event
        if (event === "SIGNED_IN" && session) {
          setSessionReady(true);
        }
      }
    );

    // Timeout de sécurité : si après 10s pas de session, afficher une erreur
    const timeout = setTimeout(() => {
      setSessionReady((prev) => {
        if (!prev) setError("La session a expiré ou le lien est invalide. Demandez un nouveau lien de réinitialisation.");
        return prev;
      });
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // ─── Soumission du nouveau mot de passe ───
  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setSuccess(true);
      // Déconnexion propre puis redirection vers le login du bon sous-domaine
      setTimeout(() => {
        supabase.auth.signOut().then(() => {
          window.location.href = "/";
        });
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    border: "1.5px solid #E5E9EF",
    borderRadius: 10,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    color: NAVY,
    fontFamily: "'Inter',sans-serif",
  };

  return (
    <div style={{ minHeight: "100vh", background: NAVY, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif" }}>
      <div style={{ position: "fixed", inset: 0, zIndex: 0, background: "radial-gradient(ellipse at 30% 50%,rgba(228,125,58,0.1) 0%,transparent 60%)" }} />

      <div style={{ position: "relative", zIndex: 1, background: "#fff", borderRadius: 16, padding: "48px 40px", width: "100%", maxWidth: 420, boxShadow: "0 24px 80px rgba(0,0,0,0.4)" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-block", width: 64, height: 64 }}>
            <svg viewBox="800 0 6000 5200" width="64" height="64" xmlns="http://www.w3.org/2000/svg">
              <path fill={NAVY} d="M1130.3,1988.73l1867.63,2832.23c199.47,302.49,644.09,299.99,840.14-4.74l2154.25-3348.45h-517.31c-275.08,0-530.85,141.38-677.18,374.31l-1362.22,2168.43L1772.35,1588.94c-102.81-149.69-306.97-188.71-457.74-87.49l-99.88,67.05c-138.74,93.14-176.43,280.72-84.44,420.22Z" />
              <path fill="#E47D3A" d="M5257.45,1005.98l447.91-661.52c80.44-118.8,214.57-189.97,358.05-189.97h698.37c21.65,0,34.71,23.97,22.96,42.16l-413.84,640.78c-85.04,131.67-231.08,211.21-387.83,211.21h-702.98c-21.92,0-34.92-24.51-22.63-42.66Z" />
            </svg>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: NAVY, marginTop: 8 }}>Bati Viz</div>
          <div style={{ fontSize: 13, color: GRAY, marginTop: 4 }}>Réinitialisation du mot de passe</div>
        </div>

        {/* ─── État : succès ─── */}
        {success ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginBottom: 10 }}>Mot de passe mis à jour !</div>
            <div style={{ fontSize: 13, color: GRAY, lineHeight: 1.6 }}>Votre mot de passe a été modifié avec succès.<br />Redirection dans 3 secondes...</div>
          </div>

        /* ─── État : en attente de session ─── */
        ) : !sessionReady && !error ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{
              width: 40, height: 40, margin: "0 auto 16px",
              border: `4px solid ${ACCENT}`, borderTopColor: "transparent",
              borderRadius: "50%", animation: "spin 1s linear infinite",
            }} />
            <div style={{ fontSize: 14, color: GRAY }}>Vérification du lien en cours...</div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>

        /* ─── État : formulaire (session prête) ─── */
        ) : (
          <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 6 }}>Nouveau mot de passe</div>
              <div style={{ fontSize: 13, color: GRAY }}>Choisissez un nouveau mot de passe sécurisé (minimum 8 caractères).</div>
            </div>
            <form onSubmit={handleReset}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Nouveau mot de passe</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={8} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlur={(e) => (e.target.style.borderColor = "#E5E9EF")}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Confirmer le mot de passe</label>
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••" required
                  style={{ ...inputStyle, borderColor: confirm && password !== confirm ? "#fca5a5" : "#E5E9EF" }}
                  onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlur={(e) => (e.target.style.borderColor = confirm && password !== confirm ? "#fca5a5" : "#E5E9EF")}
                />
                {confirm && password !== confirm && (
                  <div style={{ fontSize: 11, color: RED, marginTop: 4 }}>Les mots de passe ne correspondent pas</div>
                )}
              </div>
              {/* Indicateur force mot de passe */}
              {password && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: GRAY, marginBottom: 4 }}>Force du mot de passe :</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[password.length >= 8, /[A-Z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)].map((ok, i) => (
                      <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: ok ? (i < 2 ? ACCENT : GREEN) : "#E5E9EF", transition: "background 0.2s" }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 3 }}>8 caractères min · Majuscule · Chiffre · Caractère spécial</div>
                </div>
              )}
              {/* Erreur */}
              {error && (
                <div style={{ background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: RED, marginBottom: 16 }}>
                  ⚠️ {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !password || password !== confirm}
                style={{
                  width: "100%", padding: "13px",
                  background: loading || !password || password !== confirm ? "#94A3B8" : NAVY,
                  color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: loading || !password || password !== confirm ? "not-allowed" : "pointer",
                  boxShadow: loading || !password || password !== confirm ? "none" : "0 4px 14px rgba(13,27,42,0.35)",
                  fontFamily: "'Inter',sans-serif",
                }}
              >
                {loading ? "Mise à jour..." : "Enregistrer le nouveau mot de passe →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}